import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ExternalLink, MapPin, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { PageHeader } from '../components/AppShell';
import { ImageUploadField } from '../components/ImageUploadField';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Textarea } from '../components/ui/textarea';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';
import {
  useDeleteEvent,
  useDeleteEventTranslation,
  useEvent,
  useUpdateEvent,
  useUpsertEventTranslation,
} from '../lib/api/events';
import { fmtDateTime } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import type { EventInput, EventStatus } from '../lib/types';

const baseSchema = z.object({
  status: z.enum(['draft', 'published', 'cancelled']),
  startAt: z.string().min(1),
  endAt: z.string().optional().or(z.literal('')),
  venue: z.string().optional().or(z.literal('')),
  venueLat: z.string().optional().or(z.literal('')),
  venueLon: z.string().optional().or(z.literal('')),
  registrationUrl: z.string().url().or(z.literal('')).optional(),
});
type BaseForm = z.input<typeof baseSchema>;

const trSchema = z.object({
  title: z.string().min(1).max(512),
  summary: z.string().optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
  imageUrl: z.string().url().or(z.literal('')).optional(),
});
type TrForm = z.input<typeof trSchema>;

export default function EventDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const locale = useLocaleStore((s) => s.current);
  const available = useLocaleStore((s) => s.available);
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();

  const detail = useEvent(slug ?? null, locale);
  const update = useUpdateEvent();
  const del = useDeleteEvent();
  const upsertTr = useUpsertEventTranslation();
  const deleteTr = useDeleteEventTranslation();

  const [activeLocale, setActiveLocale] = useState<string>(locale);

  const baseForm = useForm<BaseForm>({
    resolver: zodResolver(baseSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: detail.data
      ? {
          status: detail.data.status,
          startAt: detail.data.startAt.slice(0, 16),
          endAt: detail.data.endAt ? detail.data.endAt.slice(0, 16) : '',
          venue: detail.data.venue ?? '',
          venueLat: detail.data.venueLat !== null ? String(detail.data.venueLat) : '',
          venueLon: detail.data.venueLon !== null ? String(detail.data.venueLon) : '',
          registrationUrl: detail.data.registrationUrl ?? '',
        }
      : {
          status: 'draft',
          startAt: '',
          endAt: '',
          venue: '',
          venueLat: '',
          venueLon: '',
          registrationUrl: '',
        },
  });

  const existingTr = detail.data?.translations?.find((t) => t.locale === activeLocale) ?? null;

  const trValues = {
    title: existingTr?.title ?? '',
    summary: existingTr?.summary ?? '',
    description: existingTr?.description ?? '',
    imageUrl: existingTr?.imageUrl ?? '',
  };

  const trForm = useForm<TrForm>({
    resolver: zodResolver(trSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: trValues,
  });

  // Switching locale shows a different record, not a refetch of the same
  // one, so start clean: `keepDirtyValues` above would otherwise carry
  // text typed for one locale into the next.
  useEffect(() => {
    trForm.reset(trValues, { keepDirtyValues: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocale]);

  return (
    <>
      <Topbar title={detail.data?.title ?? 'Event'} subtitle={slug} />
      <div className="px-6 py-3">
        <Link
          to="/events"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Back to events
        </Link>
      </div>
      {detail.isLoading ? (
        <div className="px-6 pt-6">
          <Spinner label="Loading…" />
        </div>
      ) : !detail.data ? (
        <div className="px-6 py-10">
          <Empty title="Event not found" />
        </div>
      ) : (
        <>
          <PageHeader
            title={detail.data.title}
            subtitle={fmtDateTime(detail.data.startAt)}
            actions={
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    detail.data.status === 'published'
                      ? 'success'
                      : detail.data.status === 'cancelled'
                        ? 'danger'
                        : 'warning'
                  }
                >
                  {detail.data.status}
                </Badge>
                <Button
                  variant="ghost"
                  className="text-danger hover:bg-danger/10"
                  onClick={async () => {
                    if (
                      !(await confirm({
                        title: `Delete event "${detail.data?.title}"?`,
                        description: "This permanently deletes the event and all its translations. This can't be undone.",
                        confirmLabel: 'Delete',
                      }))
                    )
                      return;
                    try {
                      await del.mutateAsync(detail.data!.id);
                      showToast({ title: 'Event deleted', variant: 'success' });
                      navigate('/events');
                    } catch (err) {
                      showToast({
                        title: 'Delete failed',
                        description: (err as Error).message,
                        variant: 'danger',
                      });
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              </div>
            }
          />

          <div className="grid grid-cols-1 gap-6 px-6 pb-10 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Schedule & venue</CardTitle>
                <CardDescription>Updates the canonical event record.</CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={baseForm.handleSubmit(async (v) => {
                    const input: Partial<EventInput> = {
                      status: v.status as EventStatus,
                      startAt: new Date(v.startAt).toISOString(),
                      endAt: v.endAt ? new Date(v.endAt).toISOString() : null,
                      venue: v.venue || null,
                      venueLat: v.venueLat || null,
                      venueLon: v.venueLon || null,
                      registrationUrl: v.registrationUrl || null,
                    };
                    try {
                      await update.mutateAsync({ id: detail.data!.id, input });
                      showToast({ title: 'Event updated', variant: 'success' });
                    } catch (err) {
                      showToast({
                        title: 'Save failed',
                        description: (err as Error).message,
                        variant: 'danger',
                      });
                    }
                  })}
                  className="grid gap-4"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Status</Label>
                      <Select
                        value={baseForm.watch('status')}
                        onValueChange={(v) => baseForm.setValue('status', v as EventStatus, { shouldDirty: true })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Registration URL</Label>
                      <Input {...baseForm.register('registrationUrl')} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Starts at</Label>
                      <Input type="datetime-local" {...baseForm.register('startAt')} />
                    </div>
                    <div>
                      <Label>Ends at</Label>
                      <Input type="datetime-local" {...baseForm.register('endAt')} />
                    </div>
                  </div>

                  <div>
                    <Label>Venue</Label>
                    <Input {...baseForm.register('venue')} placeholder="Radio House, Abuja" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Latitude</Label>
                      <Input {...baseForm.register('venueLat')} />
                    </div>
                    <div>
                      <Label>Longitude</Label>
                      <Input {...baseForm.register('venueLon')} />
                    </div>
                  </div>

                  {detail.data.venueLat !== null && detail.data.venueLon !== null ? (
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${detail.data.venueLat}&mlon=${detail.data.venueLon}#map=15/${detail.data.venueLat}/${detail.data.venueLon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-brand hover:underline"
                    >
                      <MapPin className="h-3 w-3" /> Open in OpenStreetMap{' '}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}

                  <div className="flex justify-end">
                    <Button type="submit" disabled={update.isPending}>
                      {update.isPending ? 'Saving…' : 'Save event'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Translations</CardTitle>
                    <CardDescription>Per-locale title, summary and description.</CardDescription>
                  </div>
                  <Select value={activeLocale} onValueChange={setActiveLocale}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {available.map((l) => (
                        <SelectItem key={l.code} value={l.code}>
                          {l.code.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={trForm.handleSubmit(async (v) => {
                    try {
                      await upsertTr.mutateAsync({
                        id: detail.data!.id,
                        locale: activeLocale,
                        input: {
                          title: v.title,
                          summary: v.summary || null,
                          description: v.description || null,
                          imageUrl: v.imageUrl || null,
                        },
                      });
                      showToast({ title: 'Translation saved', variant: 'success' });
                    } catch (err) {
                      showToast({
                        title: 'Save failed',
                        description: (err as Error).message,
                        variant: 'danger',
                      });
                    }
                  })}
                  className="grid gap-4"
                >
                  <div>
                    <Label>Title</Label>
                    <Input {...trForm.register('title')} />
                  </div>
                  <div>
                    <Label>Summary</Label>
                    <Textarea rows={2} {...trForm.register('summary')} />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea rows={5} {...trForm.register('description')} />
                  </div>
                  <div>
                    <Label>Cover image</Label>
                    <ImageUploadField
                      value={trForm.watch('imageUrl') ?? ''}
                      onChange={(url) =>
                        trForm.setValue('imageUrl', url, { shouldDirty: true })
                      }
                      category="event-cover"
                      maxDimension={1920}
                      hint="Wide 16:9 (1920×1080 ideal) — used as the hero on the event detail screen."
                    />
                  </div>
                  <div className="flex items-center justify-between border-t border-line pt-3">
                    {existingTr ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-danger hover:bg-danger/10"
                        onClick={async () => {
                          if (
                            !(await confirm({
                              title: `Remove ${activeLocale.toUpperCase()} translation?`,
                              description: 'The other translations and the event itself are kept.',
                              confirmLabel: 'Remove',
                            }))
                          )
                            return;
                          try {
                            await deleteTr.mutateAsync({
                              id: detail.data!.id,
                              locale: activeLocale,
                            });
                            showToast({ title: 'Translation removed', variant: 'success' });
                            trForm.reset(
                              { title: '', summary: '', description: '', imageUrl: '' },
                              // The translation is gone: clear every field,
                              // including ones the user had edited.
                              { keepDirtyValues: false },
                            );
                          } catch (err) {
                            showToast({
                              title: 'Delete failed',
                              description: (err as Error).message,
                              variant: 'danger',
                            });
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" /> Remove translation
                      </Button>
                    ) : (
                      <span />
                    )}
                    <Button type="submit" disabled={upsertTr.isPending}>
                      {upsertTr.isPending ? 'Saving…' : existingTr ? 'Save' : 'Add translation'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </>
      )}
      {confirmDialog}
    </>
  );
}
